import React from 'react';

/**
 * Shared "Deposit Information" + "Client Options" + "Agent Notes" section,
 * previously duplicated near-verbatim across every component form file
 * (HotelForm, FlightForm, CarRentalForm, etc). Extracted here so the new
 * agentNotes/agentCommission fields (Quote Builder Phase 2 - see optionUtils.js)
 * only need to be added in one place instead of ~15.
 *
 * Takes the same `formData`/`setFormData` a component form already keeps in
 * its own useState - purely a rendering extraction, no behavior change to
 * any of the pre-existing fields (depositRequired/depositAmount/
 * depositDueDate/isOption/optionDescription/paymentTiming/
 * optionalDepositAmount all work exactly as before).
 */
const CommonOptionFields = ({ formData, setFormData }) => {
  return (
    <>
      <div className="form-section">
        <h4>Deposit Information</h4>

        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.depositRequired}
                onChange={(e) => setFormData({ ...formData, depositRequired: e.target.checked })}
              />
              Deposit Required
            </label>
          </div>
        </div>

        {formData.depositRequired && (
          <div className="form-row">
            <div className="form-group">
              <label>Deposit Amount*</label>
              <input
                type="number"
                value={formData.depositAmount}
                onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })}
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="form-group">
              <label>Deposit Due Date</label>
              <input
                type="date"
                value={formData.depositDueDate}
                onChange={(e) => setFormData({ ...formData, depositDueDate: e.target.value })}
              />
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h4>Client Options</h4>

        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isOption}
                onChange={(e) => setFormData({ ...formData, isOption: e.target.checked })}
              />
              Mark as Optional for Client
            </label>
            <small className="form-help">
              When checked, clients can choose whether to include this component in their booking
            </small>
          </div>
        </div>

        {formData.isOption && (
          <>
            <div className="form-group">
              <label>Option Description for Client</label>
              <textarea
                value={formData.optionDescription}
                onChange={(e) => setFormData({ ...formData, optionDescription: e.target.value })}
                placeholder="e.g., Choose your preferred hotel accommodation level"
                rows="2"
              />
              <small className="form-help">
                This description helps clients understand what this option includes
              </small>
            </div>

            <div className="form-group">
              <label>Payment Timing for This Option</label>
              <select
                name="paymentTiming"
                value={formData.paymentTiming}
                onChange={(e) => setFormData({ ...formData, paymentTiming: e.target.value })}
                className="form-control"
              >
                <option value="immediate">Pay Immediately When Selected (Full Price)</option>
                <option value="with_deposit">Pay Deposit When Selected</option>
                <option value="with_final">Pay With Final Payment</option>
                <option value="bundled">Bundled (No Additional Payment)</option>
              </select>
              <small className="form-help">
                Controls when payment is due if client selects this option
              </small>
            </div>

            {formData.paymentTiming === 'with_deposit' && (
              <div className="form-group">
                <label>Deposit Amount for This Option ($)</label>
                <input
                  type="number"
                  name="optionalDepositAmount"
                  value={formData.optionalDepositAmount}
                  onChange={(e) => setFormData({ ...formData, optionalDepositAmount: e.target.value })}
                  step="0.01"
                  min="0"
                  className="form-control"
                  placeholder="0.00"
                />
                <small className="form-help">
                  Amount due immediately when client selects this option
                </small>
              </div>
            )}
          </>
        )}
      </div>

      <div className="form-section">
        <h4>Agent Notes &amp; Commission</h4>
        <p className="form-help" style={{ marginBottom: '12px' }}>
          Internal only - never shown to the client.
        </p>

        <div className="form-group">
          <label>Agent Notes</label>
          <textarea
            value={formData.agentNotes || ''}
            onChange={(e) => setFormData({ ...formData, agentNotes: e.target.value })}
            placeholder="e.g., Supplier confirmed via phone, room upgrade pending..."
            rows="2"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Your Commission ($)</label>
            <input
              type="number"
              value={formData.agentCommission ?? ''}
              onChange={(e) => setFormData({ ...formData, agentCommission: e.target.value === '' ? null : e.target.value })}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
            <small className="form-help">
              Informational only for now - does not create a commission record automatically.
            </small>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommonOptionFields;
